'use client';

import { useRef, useEffect } from 'react';

/**
 * Six-cell numeric code input with auto-advance, backspace navigation, and
 * paste support. Used by /confirm-email, /login (Clerk Client Trust step),
 * and /reset-password-with-code — extracted because all three had identical
 * copies of the same handlers and JSX.
 *
 * Controlled by the parent: parent owns `value` (a 6-string array) and
 * receives updates via `onChange`. When all 6 cells fill (typing the last
 * digit or pasting a 6-digit string) the component calls
 * `onComplete(joinedCode)` so the parent can fire its verify call.
 *
 * Focuses the first cell on mount, and again whenever `value` transitions
 * from non-empty to all-empty (i.e. parent called `setCode(['',…,''])` to
 * retry after a wrong code).
 */
export default function CodeInputGrid({
  value,
  onChange,
  onComplete,
  loading = false,
  autoFocusOnMount = true,
}) {
  const inputRefs = useRef([]);
  const wasEmpty = useRef(true);

  useEffect(() => {
    if (autoFocusOnMount) {
      inputRefs.current[0]?.focus();
    }
    // Mount-only — autoFocusOnMount flips are not a re-focus signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isEmpty = value.every((d) => !d);
    if (isEmpty && !wasEmpty.current) {
      inputRefs.current[0]?.focus();
    }
    wasEmpty.current = isEmpty;
  }, [value]);

  const handleChange = (index, raw) => {
    if (raw && !/^\d$/.test(raw)) return;

    const next = [...value];
    next[index] = raw;
    onChange(next);

    if (raw && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (index === 5 && raw && next.every((d) => d)) {
      onComplete?.(next.join(''));
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace') {
      if (!value[index] && index > 0) {
        const next = [...value];
        next[index - 1] = '';
        onChange(next);
        inputRefs.current[index - 1]?.focus();
      } else if (value[index]) {
        const next = [...value];
        next[index] = '';
        onChange(next);
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (event.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pasted)) {
      const next = pasted.split('');
      onChange(next);
      inputRefs.current[5]?.focus();
      onComplete?.(pasted);
    }
  };

  return (
    <div className="code-input-container">
      <div className="code-input-group" onPaste={handlePaste}>
        {value.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(event) => handleChange(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            className="code-digit-input"
            disabled={loading}
            aria-label={`Code digit ${index + 1}`}
          />
        ))}
      </div>
      {loading && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <div className="loading-spinner" />
          <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            Verifying...
          </span>
        </div>
      )}
    </div>
  );
}
